import { useRef, useEffect, useCallback, useMemo } from "react";
import DraggablePlayerItem from "./playerItem";
import PositionFilter from "./positionFilter";
import { POSITIONS, POSITION_LABELS } from "../functions";
import { useIsMobileBreakpoint } from "../../../hooks/useIsMobileBreakpoint";
import "./playerList.sass";

const MOBILE_BREAKPOINT = 480;
const CAROUSEL_BREAKPOINT = 780;
const MIN_THUMB_SIZE = 30;

/**
 * Calcula la posición y tamaño del thumb del scrollbar
 * @param {HTMLElement} scrollEl - Elemento con scroll
 * @param {HTMLElement} thumbEl - Elemento del thumb
 * @param {boolean} isMobile - Si es vista mobile (scroll horizontal)
 */
function updateScrollbarThumb(scrollEl, thumbEl, isMobile) {
  if (!scrollEl || !thumbEl) return;

  if (isMobile) {
    // Scroll horizontal
    const { scrollLeft, scrollWidth, clientWidth } = scrollEl;
    const hasOverflow = scrollWidth > clientWidth;
    thumbEl.parentElement.style.visibility = hasOverflow ? "visible" : "hidden";
    if (!hasOverflow) return;

    const thumbWidth = Math.max(MIN_THUMB_SIZE, (clientWidth / scrollWidth) * clientWidth);
    const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * (clientWidth - thumbWidth);
    thumbEl.style.width = `${thumbWidth}px`;
    thumbEl.style.height = "100%";
    thumbEl.style.transform = `translateX(${thumbLeft}px)`;
  } else {
    // Scroll vertical
    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    const hasOverflow = scrollHeight > clientHeight;
    thumbEl.parentElement.style.visibility = hasOverflow ? "visible" : "hidden";
    if (!hasOverflow) return;

    const thumbHeight = Math.max(MIN_THUMB_SIZE, (clientHeight / scrollHeight) * clientHeight);
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight);
    thumbEl.style.height = `${thumbHeight}px`;
    thumbEl.style.width = "100%";
    thumbEl.style.transform = `translateY(${thumbTop}px)`;
  }
}

/**
 * Construye el className para un grupo de posición
 * @param {boolean} isInactiveRole - Si el rol está inactivo
 * @param {boolean} isActive - Si el grupo está activo (drag o slot activo)
 * @returns {string} className construido
 */
function getGroupClassName(isInactiveRole, isActive) {
  return [
    "player-list__group",
    isInactiveRole && "player-list__group--dimmed",
    isActive && "player-list__group--drag-active",
  ].filter(Boolean).join(" ");
}

/**
 * Renderiza el contenido de posiciones y jugadores (compartido entre carousel y list)
 */
function PlayerGroupsContent({ playersByPosition, positionMatches, positionFilter, draggingPlayer, activeSlot, currentSlotPlayerId, accentColor, nameColor, lastNameColor, showClubs, showNationality, selectionPath, disableDrag, handlePlayerClick, isCarouselView }) {
  return POSITIONS
    .filter((pos) => positionFilter === "all" || pos === positionFilter)
    .map((pos) => {
      const group = playersByPosition[pos];
      const { hasDragMatch, hasActiveMatch } = positionMatches[pos];
      const isInactiveRole = draggingPlayer
        ? !hasDragMatch
        : (activeSlot && !hasActiveMatch);
      const isActive = draggingPlayer ? hasDragMatch : hasActiveMatch;
      const groupClassName = getGroupClassName(isInactiveRole, isActive);

      return (
        <div key={pos}
          className={isCarouselView ? `${groupClassName} player-list__group--carousel` : groupClassName}
          role="listitem"
          aria-label={`Grupo de ${POSITION_LABELS[pos] ?? pos}s`}
        >
          {positionFilter === "all" &&
            <h4 className={isCarouselView ? "player-list__group-title player-list__group-title--carousel" : "player-list__group-title"}
              style={isActive && accentColor ? { background: accentColor, color: "var(--color-white)" } : undefined}
              hidden={!isCarouselView && positionFilter !== "all"}
              id={`position-${pos}`}
            >
              {POSITION_LABELS[pos] ?? pos}s
            </h4>
          }
          <ul className={isCarouselView ? "player-list__items player-list__items--carousel" : "player-list__items"} role="list" aria-labelledby={positionFilter === "all" ? `position-${pos}` : undefined}>
            {group.map((player) => {
              const isCurrentSlot = player.id === currentSlotPlayerId;
              const isClickable = true;

              return (
                <DraggablePlayerItem
                  {...{ player, isCurrentSlot, isClickable, accentColor, nameColor, lastNameColor, showClubs, showNationality, selectionPath }}
                  key={player.id}
                  isAssigned={false}
                  disableDrag={disableDrag}
                  onClick={() => handlePlayerClick(player)}
                />
              );
            })}
          </ul>
        </div>
      );
    });
}

/**
 * Lista de jugadores disponibles con filtro, scroll personalizado y drag & drop
 * 
 * Renderiza una lista de jugadores organizados por posición con:
 * - Filtro de posiciones (todos, porteros, defensas, mediocampistas, delanteros)
 * - Scroll personalizado con thumb visual
 * - Soporte completo para drag & drop
 * - Validación de compatibilidad de roles
 * - Auto-asignación inteligente
 * - Navegación por teclado accesible
 * - Indicadores visuales de estado (asignado, actual, compatible)
 * 
 * @component
 * @example
 * // Uso básico
 * <PlayerList
 *   players={players}
 *   assignments={assignments}
 *   activeSlot={activeSlot}
 *   onAssign={handleAssign}
 *   onAutoAssign={handleAutoAssign}
 *   onClear={handleClear}
 *   accentColor="#FF0000"
 *   nameColor="#000000"
 *   lastNameColor="#333333"
 *   draggingPlayer={draggingPlayer}
 * />
 * 
 * @param {Object} props - Props del componente
 * @param {Array<Object>} props.players - Array de jugadores disponibles
 * @param {string} props.players[].id - ID único del jugador
 * @param {string} props.players[].firstName - Primer nombre del jugador
 * @param {string} props.players[].lastName - Apellido del jugador
 * @param {string} props.players[].photo - URL de la foto del jugador
 * @param {string} props.players[].position - Posición del jugador (GK, DEF, MID, FWD)
 * @param {Array<string>} props.players[].roles - Array de roles que puede jugar (ej: ["POR", "LD"])
 * 
 * @param {Object} props.assignments - Objeto con asignaciones actuales
 * @param {string} props.assignments[slotId] - Jugador asignado a cada slot (null si vacío)
 * 
 * @param {Object|null} props.activeSlot - Slot actualmente seleccionado
 * @param {string} props.activeSlot.id - ID del slot activo
 * @param {string} props.activeSlot.roleKey - Rol requerido del slot
 * @param {string} props.activeSlot.label - Etiqueta del slot (ej: "POR", "LD")
 * 
 * @param {Function} props.onAssign - Callback para asignar jugador a slot específico
 * @param {string} props.onAssign.slotId - ID del slot destino
 * @param {Object} props.onAssign.player - Jugador a asignar
 * 
 * @param {Function} props.onAutoAssign - Callback para auto-asignar jugador al mejor slot disponible
 * @param {Object} props.onAutoAssign.player - Jugador a auto-asignar
 * @returns {boolean} - true si se asignó, false si no hay slot compatible
 * 
 * @param {Function} props.onClear - Callback para limpiar un slot
 * @param {string} props.onClear.slotId - ID del slot a limpiar
 * 
 * @param {string} [props.accentColor] - Color de acento para elementos activos (hex o CSS var)
 * @param {string} [props.nameColor] - Color del nombre del jugador (hex o CSS var)
 * @param {string} [props.lastNameColor] - Color del apellido del jugador (hex o CSS var)
 * 
 * @param {Object|null} props.draggingPlayer - Jugador siendo arrastrado (null si no hay drag activo)
 * @param {string} props.draggingPlayer.id - ID del jugador siendo arrastrado
 * @param {string} props.draggingPlayer.firstName - Primer nombre
 * @param {string} props.draggingPlayer.lastName - Apellido
 * @param {Array<string>} props.draggingPlayer.roles - Roles del jugador siendo arrastrado
 * @param {string} props.draggingPlayer.roleKey - Rol principal (fallback)
 * @param {string} props.draggingPlayer.posicion - Posición (fallback si no hay roleKey)
 * 
 * @returns {JSX.Element} Elemento div con la lista de jugadores
 * 
 * @throws {Error} Si players no es un array
 * @throws {Error} Si assignments no es un objeto
 * 
 * @see {@link DraggablePlayerItem} - Componente individual de jugador
 * @see {@link PositionFilter} - Componente de filtro de posiciones
 */
export default function PlayerList({ players, assignments, activeSlot, onAssign, onAutoAssign, onClear, accentColor, nameColor, lastNameColor, draggingPlayer, positionFilter, onPositionFilterChange, disableDrag, showClubs, showNationality, selectionPath }) {
  const scrollRef = useRef(null);
  const thumbRef = useRef(null);
  const positionFilterRef = useRef(null);
  const isCarouselView = useIsMobileBreakpoint(CAROUSEL_BREAKPOINT);

  const assignedIds = new Set(
    Object.values(assignments).filter(Boolean).map((p) => p.id)
  );
  const currentSlotPlayerId = activeSlot ? assignments[activeSlot.id]?.id : null;

  // ── Scrollbar custom ─────────────────────────────────────────────
  const updateThumb = useCallback(() => {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    updateScrollbarThumb(scrollRef.current, thumbRef.current, isMobile);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateThumb();
    el.addEventListener("scroll", updateThumb);
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    // Recalcula al cambiar orientación/tamaño (desktop ↔ mobile)
    window.addEventListener("resize", updateThumb);
    return () => {
      el.removeEventListener("scroll", updateThumb);
      ro.disconnect();
      window.removeEventListener("resize", updateThumb);
    };
  }, [updateThumb, players, assignments]);
  // ────────────────────────────────────────────────────────────────

  const handlePlayerClick = (player) => {
    // Cerrar el dropdown cuando se hace click en un jugador (importante para iframes)
    positionFilterRef.current?.closeDropdown?.();
    
    // Si hay un slot activo seleccionado → asigna a ese slot (comportamiento existente)
    if (activeSlot) {
      // COMENTADO: Validación de roleKey específico
      // if (!player.roles?.includes(activeSlot.roleKey)) return;
      if (player.id === currentSlotPlayerId) {
        onClear(activeSlot.id);
      } else {
        onAssign(activeSlot.id, player);
      }
      return;
    }

    // Sin slot activo → auto-asignación inteligente
    // Si ya está asignado en algún slot, lo quita
    const assignedSlotId = Object.entries(assignments).find(([, p]) => p?.id === player.id)?.[0];
    if (assignedSlotId) {
      onClear(assignedSlotId);
      return;
    }

    // Busca el mejor slot vacío disponible
    onAutoAssign(player);
  };

  // Memoiza los grupos de jugadores por posición (evita filtrar en cada render)
  const playersByPosition = useMemo(() => {
    const grouped = {};
    POSITIONS.forEach((pos) => {
      grouped[pos] = players.filter((p) => p.position === pos && !assignedIds.has(p.id));
    });
    return grouped;
  }, [players, assignedIds]);

  // Memoiza los cálculos de compatibilidad por posición
  // Sin restricción de posición: todos los grupos siempre están activos
  const positionMatches = useMemo(() => {
    const matches = {};
    POSITIONS.forEach((pos) => {
      matches[pos] = {
        hasDragMatch: !!draggingPlayer,
        hasActiveMatch: !!activeSlot,
      };
    });
    return matches;
  }, [draggingPlayer, activeSlot]);

  return (
    <div className="player-list" role="region" aria-label="Lista de jugadores disponibles">
      {/* Filtro de posición */}
      <PositionFilter
        ref={positionFilterRef}
        value={positionFilter}
        onChange={onPositionFilterChange}
        accentColor={accentColor}
      />

      <div className={isCarouselView ? "player-list__scroll-wrapper player-list__scroll-wrapper--carousel" : "player-list__scroll-wrapper"} role="presentation">
        <div className={isCarouselView ? "player-list__scroll-content player-list__scroll-content--carousel" : "player-list__scroll-content"} ref={scrollRef} role="list" aria-label={isCarouselView ? "Jugadores disponibles en carrusel" : "Jugadores disponibles por posición"}>
          <PlayerGroupsContent
            playersByPosition={playersByPosition}
            positionMatches={positionMatches}
            positionFilter={positionFilter}
            draggingPlayer={draggingPlayer}
            activeSlot={activeSlot}
            currentSlotPlayerId={currentSlotPlayerId}
            accentColor={accentColor}
            nameColor={nameColor}
            lastNameColor={lastNameColor}
            showClubs={showClubs}
            showNationality={showNationality}
            selectionPath={selectionPath}
            disableDrag={disableDrag}
            handlePlayerClick={handlePlayerClick}
            isCarouselView={isCarouselView}
          />
        </div>

        <div className={isCarouselView ? "player-list__scrollbar-track player-list__scrollbar-track--carousel" : "player-list__scrollbar-track"} role="presentation" aria-hidden="true">
          <div
            className="player-list__scrollbar-thumb"
            ref={thumbRef}
            style={accentColor ? { background: "var(--color-black)" } : undefined}
            role="presentation"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}